import { Breakpoint, Color, FontWeight, StyledComponent } from '@on-arte/ui';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

export enum BannerTheme {
  Dark = 'dark',
  Orange = 'orange',
}

interface BannerThemeProps {
  $bannerTheme: BannerTheme;
}

export const Container: StyledComponent<'div'> = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px 40px 0;

  @media ${Breakpoint.Mobile} {
    flex-direction: column;
    padding: 16px 20px 0;
  }
`;

const bannerBackgrounds: Record<BannerTheme, FlattenSimpleInterpolation> = {
  [BannerTheme.Dark]: css`background-color: ${Color.Black};`,
  [BannerTheme.Orange]: css`background-color: ${Color.Orange};`,
};

/**
 * Ozdobna gwiazdka marki jako zwykły obrazek, nie tło CSS.
 *
 * Kryterium odbioru poz. 15 wymaga, by obrazy bannerów miały niepusty atrybut alt —
 * tło CSS nie jest obrazem i nie da się go opisać. Grafika jest poza przepływem
 * i nie przechwytuje kliknięć, więc wygląd i zachowanie bannera są bez zmian.
 */
export const BannerImage: StyledComponent<'img'> = styled.img`
  position: absolute;
  top: -46px;
  right: -46px;
  width: 210px;
  height: 210px;
  /* Znak marki ma być tłem, nie konkurencją dla nagłówka — przy pełnym kryciu
     wchodził na tytuł i utrudniał czytanie. */
  opacity: 0.28;
  pointer-events: none;
`;

export const BannerLink: StyledComponent<'a', BannerThemeProps> = styled.a<BannerThemeProps>`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px 44px;
  color: ${Color.White};
  text-decoration: none;
  ${({ $bannerTheme }: BannerThemeProps): FlattenSimpleInterpolation => bannerBackgrounds[$bannerTheme]}

  @media ${Breakpoint.SmallMobile} {
    padding: 28px 24px;
  }
`;

export const BannerTitle: StyledComponent<'p'> = styled.p`
  font-weight: ${FontWeight.Medium};
  font-size: 30px;
  line-height: 38px;
  letter-spacing: -0.01em;

  @media ${Breakpoint.SmallMobile} {
    font-size: 22px;
    line-height: 28px;
  }
`;

export const BannerText: StyledComponent<'p'> = styled.p`
  margin-top: 8px;
  font-size: 16px;
  line-height: 24px;
  opacity: 0.85;
`;

/**
 * Wygląd i zachowanie jak przyciski serwisu (motywy Secondary/Primary z biblioteki
 * UI): ten sam padding, rozmiar i najechanie na kolor Error500. Element to <span>,
 * nie <button> — przycisk wewnątrz odnośnika jest niepoprawny w HTML; klik
 * obsługuje cały baner.
 */
const bannerCtaThemes: Record<BannerTheme, FlattenSimpleInterpolation> = {
  [BannerTheme.Dark]: css`
    background-color: transparent;
    color: ${Color.White};
    border: 1px solid ${Color.White};
  `,
  [BannerTheme.Orange]: css`
    background-color: ${Color.White};
    color: ${Color.Black};
    border: 1px solid ${Color.White};
  `,
};

export const BannerCta: StyledComponent<'span', BannerThemeProps> = styled.span<BannerThemeProps>`
  display: inline-block;
  margin-top: 24px;
  padding: 14px 28px;
  font-weight: ${FontWeight.Medium};
  font-size: 14px;
  line-height: 18px;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  ${({ $bannerTheme }: BannerThemeProps): FlattenSimpleInterpolation => bannerCtaThemes[$bannerTheme]}

  ${BannerLink}:hover & {
    background-color: ${Color.Error500};
    border-color: ${Color.Error500};
    color: ${Color.White};
  }
`;
